import React, { useState, useMemo } from "react";
import {
  ShoppingBag,
  Plus,
  Trash2,
  Edit2,
  Check,
  Truck,
  Archive,
  ArrowLeft,
  Search,
  AlertCircle,
  RefreshCw,
  PlusCircle,
  ChevronDown,
  ChevronUp,
  PackageCheck,
  CheckCircle2,
  X,
  ExternalLink
} from "lucide-react";
import { useSupplyPurchases } from "../hooks/useSupplyPurchases";

// Função auxiliar para formatar o fornecedor (links clicáveis e WhatsApp/ligação)
function formatSupplier(text) {
  if (!text) return null;
  const trimmed = text.trim();
  
  // Regex para checar se é um link completo ou abreviado
  const urlRegex = /^(https?:\/\/|www\.)[^\s/$.?#].[^\s]*/i;
  // Regex para checar se é um telefone
  const digits = trimmed.replace(/\D/g, "");
  const isPhone = digits.length >= 8 && digits.length <= 15 && /^[+\d\s().-]+$/.test(trimmed);

  if (urlRegex.test(trimmed)) {
    const href = trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-0.5 font-medium"
        onClick={(e) => e.stopPropagation()}
      >
        {trimmed}
        <ExternalLink size={10} />
      </a>
    );
  }

  if (isPhone) {
    const telHref = `tel:${digits}`;
    const waHref = `https://wa.me/${digits.startsWith("55") ? digits : `55${digits}`}`;
    
    return (
      <span className="inline-flex items-center gap-1.5 font-medium text-slate-500">
        <a
          href={telHref}
          className="text-blue-600 hover:text-blue-800 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {trimmed}
        </a>
        {digits.length >= 10 && (
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-600 hover:text-emerald-800 hover:underline font-bold text-[10px]"
            onClick={(e) => e.stopPropagation()}
          >
            (WhatsApp)
          </a>
        )}
      </span>
    );
  }

  // Se tiver um link no meio do texto
  const words = trimmed.split(/\s+/);
  const hasUrl = words.some(w => urlRegex.test(w));
  if (hasUrl) {
    return (
      <span>
        {words.map((word, idx) => {
          if (urlRegex.test(word)) {
            const href = word.startsWith("http") ? word : `https://${word}`;
            return (
              <React.Fragment key={idx}>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-0.5 font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  {word}
                  <ExternalLink size={8} />
                </a>{" "}
              </React.Fragment>
            );
          }
          return <span key={idx}>{word} </span>;
        })}
      </span>
    );
  }

  return <span className="font-medium text-slate-500">{trimmed}</span>;
}

export default function SupplyPurchasesSubTab({ supplies, onRefreshSupplies }) {
  const {
    purchases,
    isLoading,
    error,
    createPurchase,
    updatePurchase,
    removePurchase,
    markAsOrdered,
    receivePurchase,
  } = useSupplyPurchases();

  // Estados de Navegação
  const [activeSubTab, setActiveSubTab] = useState("list"); // 'list' ou 'form'
  const [purchaseFilter, setPurchaseFilter] = useState("all"); // 'all', 'draft', 'ordered', 'received'
  const [editingOrderId, setEditingOrderId] = useState(null);

  // Estados do Formulário de Novo/Edição de Pedido (Duas Colunas)
  const [orderItems, setOrderItems] = useState({}); // { [supplyId]: { name, unit, min_quantity, current_quantity, suggested_quantity, ordered_quantity } }
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogSector, setCatalogSector] = useState("todos");
  const [catalogCategory, setCatalogCategory] = useState("todas");
  const [catalogLowStockOnly, setCatalogLowStockOnly] = useState(false);

  // Estados do Modal de Recebimento
  const [receivingOrder, setReceivingOrder] = useState(null); // pedido que está sendo recebido
  const [receivedQtyMap, setReceivedQtyMap] = useState({}); // { [supplyId]: number }
  const [syncStatus, setSyncStatus] = useState({ active: false, currentName: "", index: 0, total: 0 });
  const [successScreen, setSuccessScreen] = useState(false);

  // Estados de Expandir/Colapsar Detalhes dos Cards
  const [expandedOrders, setExpandedOrders] = useState({});

  // 1. Motor de Alertas Automáticos: Encontra insumos abaixo do estoque mínimo
  const lowStockSupplies = useMemo(() => {
    return supplies.filter((s) => {
      const current = s.current_quantity || 0;
      const min = s.min_quantity || 0;
      return current <= min;
    });
  }, [supplies]);

  // Memos do Catálogo
  const availableSectors = useMemo(() => {
    return Array.from(new Set(supplies.map(s => s.sector).filter(Boolean))).sort();
  }, [supplies]);

  const availableCategories = useMemo(() => {
    return Array.from(new Set(supplies.map(s => s.category).filter(Boolean))).sort();
  }, [supplies]);

  const catalogFilteredSupplies = useMemo(() => {
    return supplies.filter((s) => {
      if (s.sector === "pedras") return false;
      if (orderItems[s.id]) return false; // oculta se já está no pedido

      const isLowStock = (s.current_quantity || 0) <= (s.min_quantity || 0);
      if (catalogLowStockOnly && !isLowStock) return false;

      const matchSearch = s.name.toLowerCase().includes(catalogSearch.toLowerCase());
      const matchSector = catalogSector === "todos" ? true : s.sector === catalogSector;
      const matchCategory = catalogCategory === "todas" ? true : s.category === catalogCategory;
      
      return matchSearch && matchSector && matchCategory;
    });
  }, [supplies, orderItems, catalogSearch, catalogSector, catalogCategory, catalogLowStockOnly]);

  const catalogLowStockCount = useMemo(() => {
    return supplies.filter((s) => {
      if (s.sector === "pedras") return false;
      if (orderItems[s.id]) return false; // apenas os elegíveis que não foram adicionados
      const isLowStock = (s.current_quantity || 0) <= (s.min_quantity || 0);
      return isLowStock;
    }).length;
  }, [supplies, orderItems]);

  const handleAddCatalogItem = (supply) => {
    const current = supply.current_quantity || 0;
    const min = supply.min_quantity || 0;
    const unidadesPorCompra = Number(supply.unidades_por_compra) || 1;
    
    const gap = min - current;
    const suggested = gap > 0 ? Math.ceil(gap / unidadesPorCompra) : 1;

    let meta = supply.metadata;
    if (typeof meta === "string") {
      try { meta = JSON.parse(meta); } catch (e) {}
    }
    const forn = meta?.fornecedor || "";

    setOrderItems((prev) => ({
      ...prev,
      [supply.id]: {
        supply_id: supply.id,
        name: supply.name,
        unit: supply.unit || "un",
        current_quantity: current,
        min_quantity: min,
        unidades_por_compra: unidadesPorCompra,
        suggested_quantity: gap > 0 ? suggested : 0,
        ordered_quantity: suggested,
        fornecedor: forn,
      }
    }));
  };

  // Inicializa o formulário de compras
  const handleOpenNewOrderForm = () => {
    setEditingOrderId(null);
    const initialItems = {};
    
    // Auto-carrega todos os itens abaixo do mínimo com a sugestão de compra ideal
    lowStockSupplies.forEach((s) => {
      const current = s.current_quantity || 0;
      const min = s.min_quantity || 0;
      const unidadesPorCompra = Number(s.unidades_por_compra) || 1;
      
      const gap = min - current;
      const suggested = gap > 0 ? Math.ceil(gap / unidadesPorCompra) : 1;
      
      let meta = s.metadata;
      if (typeof meta === "string") {
        try { meta = JSON.parse(meta); } catch (e) {}
      }
      const forn = meta?.fornecedor || "";
      
      initialItems[s.id] = {
        supply_id: s.id,
        name: s.name,
        unit: s.unit || "un",
        current_quantity: current,
        min_quantity: min,
        unidades_por_compra: unidadesPorCompra,
        suggested_quantity: gap > 0 ? suggested : 0,
        ordered_quantity: suggested, // pré-preenche com o sugerido em embalagens
        fornecedor: forn,
      };
    });

    setOrderItems(initialItems);
    setCatalogSearch("");
    setCatalogSector("todos");
    setCatalogCategory("todas");
    setCatalogLowStockOnly(false);
    setActiveSubTab("form");
  };

  // Inicializa a edição de um rascunho
  const handleEditDraft = (order) => {
    setEditingOrderId(order.id);
    const initialItems = {};
    const itemsArray = Array.isArray(order.items) ? order.items : [];
    
    itemsArray.forEach((item) => {
      if (item.supply_id) {
        const matchingSupply = supplies.find(s => s.id === item.supply_id);
        const upc = item.unidades_por_compra || (matchingSupply ? Number(matchingSupply.unidades_por_compra) : 1) || 1;
        
        let forn = item.fornecedor || "";
        if (!forn && matchingSupply) {
          let meta = matchingSupply.metadata;
          if (typeof meta === "string") {
            try { meta = JSON.parse(meta); } catch (e) {}
          }
          forn = meta?.fornecedor || "";
        }

        initialItems[item.supply_id] = {
          ...item,
          unidades_por_compra: upc,
          ordered_quantity: item.ordered_quantity || 0,
          fornecedor: forn,
        };
      }
    });

    setOrderItems(initialItems);
    setCatalogSearch("");
    setCatalogSector("todos");
    setCatalogCategory("todas");
    setCatalogLowStockOnly(false);
    setActiveSubTab("form");
  };

  // Remover item do formulário de compras
  const handleRemoveFormItem = (id) => {
    setOrderItems((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // Mudar quantidade ordenada de um item no formulário
  const handleUpdateFormItemQty = (id, val) => {
    setOrderItems((prev) => {
      if (!prev[id]) return prev;
      let nextVal = typeof val === "function" ? val(prev[id].ordered_quantity) : val;
      if (nextVal < 0.01) nextVal = 0.01; // não permite quantidade zero ou negativa no pedido
      return {
        ...prev,
        [id]: {
          ...prev[id],
          ordered_quantity: nextVal,
        },
      };
    });
  };

  // Salvar pedido de compras (Draft)
  const handleSaveOrder = async (e) => {
    e.preventDefault();
    const items = Object.values(orderItems);
    if (items.length === 0) {
      alert("Adicione pelo menos um item ao pedido.");
      return;
    }

    try {
      // Agrupar itens por fornecedor
      const groups = {};
      items.forEach((item) => {
        const supplier = (item.fornecedor || "").trim();
        const key = supplier === "" ? "Sem Fornecedor" : supplier;
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });

      const groupKeys = Object.keys(groups);

      if (editingOrderId) {
        // Se estamos editando, o primeiro grupo atualiza o rascunho atual
        const firstKey = groupKeys[0];
        await updatePurchase(editingOrderId, groups[firstKey]);

        // Os demais grupos criam novos rascunhos de pedidos de compra
        for (let i = 1; i < groupKeys.length; i++) {
          await createPurchase(groups[groupKeys[i]]);
        }
        
        if (groupKeys.length > 1) {
          alert(`Pedido de compras original atualizado para o fornecedor "${firstKey}" e criado(s) ${groupKeys.length - 1} novo(s) rascunho(s) para outros fornecedores!`);
        } else {
          alert("Pedido de compras atualizado!");
        }
      } else {
        // Se é um novo pedido, cria um rascunho para cada grupo
        for (const key of groupKeys) {
          await createPurchase(groups[key]);
        }
        if (groupKeys.length > 1) {
          alert(`Criados ${groupKeys.length} rascunhos de pedidos separados por fornecedor!`);
        } else {
          alert("Rascunho de pedido de compras criado!");
        }
      }
      setActiveSubTab("list");
    } catch (err) {
      alert("Erro ao salvar pedido de compra: " + err.message);
    }
  };

  // Excluir rascunho de compras
  const handleDeleteDraft = async (id, name) => {
    if (!window.confirm("Deseja realmente remover este rascunho de pedido de compras?")) return;
    try {
      await removePurchase(id);
    } catch (err) {
      alert("Erro ao remover rascunho: " + err.message);
    }
  };

  // Marcar como comprado
  const handleMarkAsOrdered = async (order) => {
    if (!window.confirm("Confirmar que a compra foi solicitada financeiramente ao fornecedor? O status passará para 'Em Trânsito'.")) return;
    try {
      // Cria campo received_quantity inicializado com a mesma quantidade comprada (default)
      const updatedItems = order.items.map((i) => ({
        ...i,
        received_quantity: i.ordered_quantity,
      }));
      await markAsOrdered(order.id, updatedItems);
      alert("Pedido marcado como comprado! Aguardando recebimento físico.");
    } catch (err) {
      alert("Erro ao atualizar status: " + err.message);
    }
  };

  // Iniciar recebimento (abre modal de conferência)
  const handleStartReceiving = (order) => {
    setReceivingOrder(order);
    const initialReceived = {};
    const itemsArray = Array.isArray(order.items) ? order.items : [];
    itemsArray.forEach((i) => {
      initialReceived[i.supply_id] = i.ordered_quantity || 0; // preenche sugerindo tudo entregue
    });
    setReceivedQtyMap(initialReceived);
  };

  // Confirmar recebimento físico de mercadoria
  const handleConfirmReceipt = async () => {
    if (!receivingOrder) return;
    setReceivingOrder(null);
    setSyncStatus({ active: true, currentName: "Iniciando recebimento...", index: 0, total: receivingOrder.items.length });

    try {
      // Prepara os itens atualizando as quantidades recebidas reais do operador
      const updatedItems = receivingOrder.items.map((item) => {
        const received = receivedQtyMap[item.supply_id] !== undefined ? receivedQtyMap[item.supply_id] : 0;
        return {
          ...item,
          received_quantity: received,
        };
      });

      // Executa a finalização e soma de estoque físico (Atomic increment)
      await receivePurchase(receivingOrder.id, updatedItems, (itemName, idx, tot) => {
        setSyncStatus({ active: true, currentName: itemName, index: idx, total: tot });
      });

      // Sucesso!
      setSyncStatus({ active: false, currentName: "", index: 0, total: 0 });
      setSuccessScreen(true);
      if (onRefreshSupplies) {
        await onRefreshSupplies(); // recarrega o grid principal
      }
    } catch (err) {
      setSyncStatus({ active: false, currentName: "", index: 0, total: 0 });
      alert("Erro ao receber mercadoria: " + err.message);
    }
  };

  // Alternar visualização de detalhes de um card de pedido
  const toggleExpandOrder = (id) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Filtra os pedidos com base no status selecionado
  const filteredPurchases = useMemo(() => {
    return purchases.filter((p) => {
      if (purchaseFilter === "all") return true;
      return p.status === purchaseFilter;
    });
  }, [purchases, purchaseFilter]);

  // Estatísticas de pedidos
  const purchaseStats = useMemo(() => {
    return {
      draft: purchases.filter((p) => p.status === "draft").length,
      ordered: purchases.filter((p) => p.status === "ordered").length,
      received: purchases.filter((p) => p.status === "received").length,
    };
  }, [purchases]);

  // --- TELA DE SUCESSO DO RECEBIMENTO ---
  if (successScreen) {
    return (
      <div className="flex flex-col items-center justify-center p-6 my-12 text-center max-w-md mx-auto bg-white rounded-2xl shadow-lg border border-emerald-100 animate-in fade-in zoom-in duration-300">
        <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center border border-emerald-100 mb-6 text-emerald-600 shadow-inner">
          <PackageCheck size={44} className="animate-bounce" />
        </div>
        <h3 className="text-2xl font-bold text-slate-800">Entrada Concluída!</h3>
        <p className="text-slate-500 mt-2 text-sm leading-relaxed">
          A mercadoria foi recebida e as quantidades confirmadas foram adicionadas ao saldo físico dos insumos no PocketBase com sucesso.
        </p>
        <button
          onClick={() => setSuccessScreen(false)}
          className="mt-8 w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold hover:shadow-md transition-all active:scale-[0.98]"
        >
          Voltar às Compras
        </button>
      </div>
    );
  }

  // --- OVERLAY DE PROGRESSO DE RECONCILIAÇÃO ---
  if (syncStatus.active) {
    const percent = Math.round((syncStatus.index / syncStatus.total) * 100) || 0;
    return (
      <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[60] flex flex-col items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-sm w-full text-center flex flex-col items-center">
          <RefreshCw className="animate-spin text-blue-600 mb-4" size={40} />
          <h4 className="text-lg font-bold text-slate-800">Atualizando Estoque Físico</h4>
          <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider font-semibold">
            Somando {syncStatus.index} de {syncStatus.total} ({percent}%)
          </p>
          <div className="w-full bg-slate-100 h-2 rounded-full mt-4 overflow-hidden">
            <div
              className="bg-blue-600 h-full rounded-full transition-all duration-300"
              style={{ width: `${percent}%` }}
            ></div>
          </div>
          <p className="text-slate-600 text-sm font-medium mt-4 truncate max-w-full italic">
            "{syncStatus.currentName}"
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden h-full bg-slate-50 w-full">
      {isLoading && purchases.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-slate-500">
          <RefreshCw className="animate-spin mr-2" /> Carregando pedidos...
        </div>
      ) : activeSubTab === "list" ? (

        // ==========================================
        // 💻 TELA 1: LISTAGEM E DASHBOARD DE PEDIDOS
        // ==========================================
        <div className="flex-1 flex flex-col overflow-hidden h-full">
          {/* Header */}
          <div className="bg-white border-b border-slate-200 p-4 shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <ShoppingBag className="text-blue-600" /> Compras & Entrada de Mercadoria
              </h2>
              <p className="text-xs text-slate-500">
                Gerencie pedidos de suprimentos ao fornecedor e receba as entregas físicas.
              </p>
            </div>
            <button
              onClick={handleOpenNewOrderForm}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl font-bold shadow transition-all active:scale-[0.98] w-full sm:w-auto justify-center"
            >
              <Plus size={18} /> Novo Pedido
            </button>
          </div>

          {/* Filtros Internos por Status */}
          <div className="bg-white border-b border-slate-200 px-4 py-2 shrink-0 flex gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden">
            <button
              onClick={() => setPurchaseFilter("all")}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                purchaseFilter === "all" ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              Todos ({purchases.length})
            </button>
            <button
              onClick={() => setPurchaseFilter("draft")}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                purchaseFilter === "draft" ? "bg-blue-600 text-white" : "text-blue-600 bg-blue-50 hover:bg-blue-100"
              }`}
            >
              Rascunhos ({purchaseStats.draft})
            </button>
            <button
              onClick={() => setPurchaseFilter("ordered")}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                purchaseFilter === "ordered" ? "bg-amber-600 text-white" : "text-amber-700 bg-amber-50 hover:bg-amber-100"
              }`}
            >
              <Truck size={12} /> Em Trânsito ({purchaseStats.ordered})
            </button>
            <button
              onClick={() => setPurchaseFilter("received")}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                purchaseFilter === "received" ? "bg-emerald-600 text-white" : "text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
              }`}
            >
              <Archive size={12} /> Recebidos ({purchaseStats.received})
            </button>
          </div>

          {/* Lista de Pedidos */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
            <div className="max-w-4xl mx-auto space-y-4">
              {filteredPurchases.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
                  <ShoppingBag size={40} className="mx-auto text-slate-300 mb-2 opacity-50" />
                  Nenhum pedido de compra encontrado neste status.
                </div>
              ) : (
                filteredPurchases.map((order) => {
                  const isExpanded = !!expandedOrders[order.id];
                  const formattedDate = new Date(order.created).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                  const itemsCount = Array.isArray(order.items) ? order.items.length : 0;

                  return (
                    <div
                      key={order.id}
                      className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden transition-all hover:shadow"
                    >
                      {/* Header do Card */}
                      <div className="p-4 sm:p-5 flex flex-col sm:flex-row justify-between sm:items-center gap-3 bg-slate-50/50 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                          {order.status === "draft" && (
                            <span className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse" title="Rascunho"></span>
                          )}
                          {order.status === "ordered" && (
                            <span className="w-2.5 h-2.5 bg-amber-500 rounded-full" title="Comprado"></span>
                          )}
                          {order.status === "received" && (
                            <span className="w-2.5 h-2.5 bg-emerald-500 rounded-full" title="Recebido"></span>
                          )}
                          <div>
                            <h4 className="font-bold text-slate-800 text-sm sm:text-base">
                              Pedido #{order.id.slice(-6).toUpperCase()}
                            </h4>
                            <span className="text-[10px] text-slate-400 block mt-0.5">
                              Criado em: {formattedDate}
                            </span>
                          </div>
                        </div>

                        {/* Status Badge e Ações Primárias */}
                        <div className="flex items-center justify-between sm:justify-end gap-3 border-t sm:border-t-0 pt-2 sm:pt-0 border-slate-100">
                          {order.status === "draft" && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditDraft(order)}
                                className="p-1.5 hover:bg-slate-200 text-slate-500 hover:text-slate-800 rounded transition-colors"
                                title="Editar Rascunho"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteDraft(order.id)}
                                className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-600 rounded transition-colors"
                                title="Remover Rascunho"
                              >
                                <Trash2 size={14} />
                              </button>
                              <button
                                onClick={() => handleMarkAsOrdered(order)}
                                className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow transition-colors"
                              >
                                <Truck size={12} /> Comprar
                              </button>
                            </div>
                          )}

                          {order.status === "ordered" && (
                            <button
                              onClick={() => handleStartReceiving(order)}
                              className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow transition-colors"
                            >
                              <CheckCircle2 size={12} /> Receber Mercadoria
                            </button>
                          )}

                          {order.status === "received" && (
                            <span className="text-xs bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-lg font-bold flex items-center gap-1">
                              ✓ RECEBIDO E CONCILIADO
                            </span>
                          )}

                          <button
                            onClick={() => toggleExpandOrder(order.id)}
                            className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                          >
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </div>
                      </div>

                      {/* Preview Rápido / Resumo das Quantidades */}
                      <div className="px-4 py-3 flex items-center justify-between text-xs text-slate-400 bg-white">
                        <span>Total de suprimentos no pedido: <strong>{itemsCount}</strong></span>
                        {!isExpanded && (
                          <button
                            onClick={() => toggleExpandOrder(order.id)}
                            className="text-blue-500 font-bold hover:underline"
                          >
                            Ver itens
                          </button>
                        )}
                      </div>

                      {/* Detalhes Expandidos (Tabela) */}
                      {isExpanded && (
                        <div className="border-t border-slate-100 bg-slate-50/30 overflow-x-auto">
                          <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-slate-100/50 text-slate-500 uppercase font-semibold">
                              <tr>
                                <th className="p-3">Suprimento</th>
                                <th className="p-3 text-right">Comprado</th>
                                {order.status === "received" && (
                                  <th className="p-3 text-right">Recebido</th>
                                )}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                               {order.items.map((item, idx) => {
                                 const upc = Number(item.unidades_por_compra) || 1;
                                 return (
                                   <tr key={idx} className="hover:bg-slate-50/50">
                                     <td className="p-3 font-medium text-slate-700">
                                       {item.name}
                                       {item.fornecedor && (
                                         <span className="text-[10px] text-slate-400 block mt-0.5">
                                           Fornecedor: {item.fornecedor}
                                         </span>
                                       )}
                                       {upc > 1 && (
                                         <span className="text-[9px] text-slate-400 block mt-0.5">
                                           Embalagem c/ {upc} {item.unit}
                                         </span>
                                       )}
                                     </td>
                                     <td className="p-3 text-right font-mono font-semibold text-slate-700 whitespace-nowrap">
                                       {item.ordered_quantity} emb. {upc > 1 && <span className="text-[9px] font-normal text-slate-400">({item.ordered_quantity * upc} {item.unit})</span>}
                                     </td>
                                     {order.status === "received" && (
                                       <td className="p-3 text-right font-mono font-bold text-emerald-600 whitespace-nowrap">
                                         {item.received_quantity} emb. {upc > 1 && <span className="text-[9px] font-normal text-slate-400">({item.received_quantity * upc} {item.unit})</span>}
                                       </td>
                                     )}
                                   </tr>
                                 );
                               })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      ) : (

        // ==========================================
        // 📝 TELA 2: FORMULÁRIO DE PEDIDO DE COMPRAS (Novo/Edição)
        // ==========================================
        <div className="flex-1 flex flex-col overflow-hidden h-full bg-white">
          {/* Header do Form */}
          <div className="bg-slate-50 border-b border-slate-200 p-4 shrink-0 flex items-center gap-3">
            <button
              onClick={() => setActiveSubTab("list")}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h3 className="font-bold text-slate-800 text-lg">
                {editingOrderId ? "Editar Pedido de Compras" : "Montar Pedido de Compras"}
              </h3>
              <p className="text-xs text-slate-500">
                {editingOrderId ? "Modifique as quantidades do rascunho ativo" : "Selecione o que deseja comprar"}
              </p>
            </div>
          </div>

          <div className="flex-1 flex flex-col md:flex-row overflow-hidden h-full">
            {/* COLUNA ESQUERDA: CATÁLOGO DE INSUMOS */}
            <div className="w-full md:w-1/2 border-r border-slate-200 bg-slate-50 flex flex-col overflow-hidden h-full">
              {/* Filtros e Busca do Catálogo */}
              <div className="p-4 bg-white border-b border-slate-200 flex flex-col gap-3 shrink-0">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Escolher Insumos para Compra
                </h4>
                
                {/* Busca */}
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    placeholder="Buscar insumo para adicionar..."
                    value={catalogSearch}
                    onChange={(e) => setCatalogSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>

                {/* Filtros lado a lado */}
                <div className="grid grid-cols-2 gap-2">
                  {/* Filtro Setor */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Setor</label>
                    <select
                      value={catalogSector}
                      onChange={(e) => setCatalogSector(e.target.value)}
                      className="px-2 py-1 border border-slate-200 rounded text-xs outline-none bg-white text-slate-700"
                    >
                      <option value="todos">Todos os Setores</option>
                      {availableSectors.map(s => (
                        <option key={s} value={s}>{s.toUpperCase()}</option>
                      ))}
                    </select>
                  </div>
                  {/* Filtro Categoria */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Categoria</label>
                    <select
                      value={catalogCategory}
                      onChange={(e) => setCatalogCategory(e.target.value)}
                      className="px-2 py-1 border border-slate-200 rounded text-xs outline-none bg-white text-slate-700"
                    >
                      <option value="todas">Todas Categorias</option>
                      {availableCategories.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Filtro Estoque Baixo */}
                <div className="flex items-center border-t border-slate-100 pt-2 shrink-0">
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={catalogLowStockOnly}
                      onChange={(e) => setCatalogLowStockOnly(e.target.checked)}
                      className="rounded border-slate-300 text-red-650 focus:ring-red-500 w-3.5 h-3.5"
                    />
                    <span className="text-[11px] font-bold text-red-650 flex items-center gap-1">
                      <AlertCircle size={12} className={catalogLowStockOnly ? "text-red-650" : "text-red-500 animate-pulse"} />
                      Apenas Estoque Baixo ({catalogLowStockCount})
                    </span>
                  </label>
                </div>
              </div>

              {/* Lista do Catálogo */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {catalogFilteredSupplies.length === 0 ? (
                  <div className="text-center py-8 text-slate-400 text-xs bg-white rounded-xl border border-slate-100">
                    Nenhum insumo disponível para adicionar.
                  </div>
                ) : (
                  catalogFilteredSupplies.map((s) => {
                    const isLowStock = (s.current_quantity || 0) <= (s.min_quantity || 0);
                    const isUnavailable = (s.current_quantity || 0) <= 0;
                    return (
                      <div
                        key={s.id}
                        className={`p-3 rounded-xl border transition-all flex justify-between items-center gap-3 shadow-xs ${
                          isUnavailable 
                            ? "bg-red-50/40 border-red-200 hover:border-red-300" 
                            : isLowStock 
                              ? "bg-amber-50/50 border-amber-200 hover:border-amber-300 animate-pulse-slow" 
                              : "bg-white border-slate-200 hover:border-blue-300"
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <span className={`font-bold text-xs flex items-center gap-1 ${
                            isUnavailable 
                              ? "text-red-700" 
                              : isLowStock 
                                ? "text-amber-800" 
                                : "text-slate-800"
                          }`}>
                            {(isUnavailable || isLowStock) && (
                              <AlertCircle size={12} className={isUnavailable ? "text-red-500 shrink-0" : "text-amber-500 shrink-0"} />
                            )}
                            {s.name}
                          </span>
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1 py-0.2 rounded uppercase">
                              {s.sector || "sem setor"}
                            </span>
                            <span className="text-[9px] font-semibold text-slate-400">
                              {s.category || ""}
                            </span>
                          </div>
                          {(() => {
                            let meta = s.metadata;
                            if (typeof meta === "string") {
                              try { meta = JSON.parse(meta); } catch (e) {}
                            }
                            const forn = meta?.fornecedor;
                            if (forn && forn.trim() !== "") {
                              return (
                                <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1 flex-wrap">
                                  <span className="font-medium text-slate-400">Fornecedor:</span>
                                  {formatSupplier(forn)}
                                </div>
                              );
                            }
                            return null;
                          })()}
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="text-[10px] text-slate-500">
                              Estoque: <strong className={
                                isUnavailable 
                                  ? "text-red-600 font-extrabold" 
                                  : isLowStock 
                                    ? "text-amber-600 font-extrabold" 
                                    : "text-slate-650"
                              }>{s.current_quantity} {s.unit}</strong>
                            </span>
                            <span className="text-[10px] text-slate-400">
                              / Mín: {s.min_quantity}
                            </span>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleAddCatalogItem(s)}
                          className={`px-2.5 py-1.5 text-white rounded-lg text-xs font-bold shadow-xs active:scale-[0.97] transition-all shrink-0 flex items-center gap-1 ${
                            isUnavailable 
                              ? "bg-red-600 hover:bg-red-700" 
                              : isLowStock 
                                ? "bg-amber-600 hover:bg-amber-700" 
                                : "bg-blue-600 hover:bg-blue-700"
                          }`}
                        >
                          <Plus size={12} />
                          <span>Adicionar</span>
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* COLUNA DIREITA: ITENS SELECIONADOS NO PEDIDO */}
            <div className="w-full md:w-1/2 flex flex-col overflow-hidden h-full relative">
              <div className="p-4 border-b border-slate-200 shrink-0 bg-white flex justify-between items-center">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Itens no Pedido ({Object.keys(orderItems).length})
                </h4>
                {Object.keys(orderItems).length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm("Deseja esvaziar o pedido?")) {
                        setOrderItems({});
                      }
                    }}
                    className="text-red-500 hover:text-red-700 text-xs font-bold"
                  >
                    Esvaziar
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                  <table className="w-full text-left text-xs bg-white border-collapse">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold">
                      <tr>
                        <th className="p-2.5">Item</th>
                        <th className="p-2.5 text-right">Estoque</th>
                        <th className="p-2.5 text-center w-28">Quantidade</th>
                        <th className="p-2.5 w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {Object.keys(orderItems).length === 0 ? (
                        <tr>
                          <td colSpan="4" className="p-8 text-center text-slate-400">
                            Nenhum insumo no pedido. Use o painel esquerdo para adicionar itens.
                          </td>
                        </tr>
                      ) : (
                        Object.values(orderItems).map((item) => {
                          const isLowStock = item.current_quantity <= item.min_quantity;
                          const upc = Number(item.unidades_por_compra) || 1;
                          return (
                            <tr key={item.supply_id} className="hover:bg-slate-50/50">
                              <td className="p-2.5">
                                <span className="font-bold text-slate-800 block truncate max-w-[140px] sm:max-w-none">{item.name}</span>
                                {item.fornecedor && (
                                  <div className="text-[9px] text-slate-500 mt-0.5 flex items-center gap-1 flex-wrap">
                                    <span className="font-medium text-slate-400">Fornecedor:</span>
                                    {formatSupplier(item.fornecedor)}
                                  </div>
                                )}
                                {upc > 1 && (
                                  <span className="text-[9px] text-slate-400 block mt-0.5">
                                    Embalagem c/ {upc} {item.unit}
                                  </span>
                                )}
                                {isLowStock && (
                                  <span className="text-[8px] bg-red-100 text-red-800 px-1 py-0.2 rounded font-bold uppercase mt-0.5 inline-block">Baixo Estoque</span>
                                )}
                              </td>
                              <td className="p-2.5 text-right font-mono text-slate-500">
                                {item.current_quantity} <span className="text-[10px] text-slate-400">{item.unit}</span>
                              </td>
                              <td className="p-2.5 text-center">
                                <div className="flex flex-col items-center">
                                  <div className="flex items-center justify-between border border-slate-200 rounded-lg p-0.5 w-full bg-slate-50 max-w-[90px]">
                                    <button
                                      type="button"
                                      onClick={() => handleUpdateFormItemQty(item.supply_id, (c) => Math.max(1, c - 1))}
                                      className="w-5 h-5 rounded bg-white text-slate-600 border active:bg-slate-100 flex items-center justify-center font-bold text-xs"
                                    >
                                      -
                                    </button>
                                    <input
                                      type="number"
                                      min="1"
                                      step="any"
                                      value={item.ordered_quantity}
                                      onChange={(e) => handleUpdateFormItemQty(item.supply_id, Math.max(1, Number(e.target.value) || 0))}
                                      className="w-8 bg-transparent text-center font-mono font-bold text-slate-850 text-xs outline-none"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleUpdateFormItemQty(item.supply_id, (c) => c + 1)}
                                      className="w-5 h-5 rounded bg-white text-slate-600 border active:bg-slate-100 flex items-center justify-center font-bold text-xs"
                                    >
                                      +
                                    </button>
                                  </div>
                                  {upc > 1 && (
                                    <span className="text-[9px] text-slate-400 mt-0.5 font-semibold">
                                      = {item.ordered_quantity * upc} {item.unit}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-2.5 text-center">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveFormItem(item.supply_id)}
                                  className="text-slate-300 hover:text-red-500 p-1"
                                >
                                  <X size={12} />
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky Bottom Actions Bar */}
          <div className="absolute bottom-0 left-0 w-full bg-white border-t border-slate-200 p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] flex items-center justify-end gap-3 z-40">
            <button
              type="button"
              onClick={() => setActiveSubTab("list")}
              className="px-5 py-2.5 font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-xl transition-colors text-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              onClick={handleSaveOrder}
              disabled={Object.keys(orderItems).length === 0}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm shadow-md hover:shadow-lg transition-all active:scale-[0.98] disabled:opacity-50"
            >
              <Check size={16} />
              {editingOrderId ? "Atualizar Pedido" : "Gravar Rascunho"}
            </button>
          </div>
        </div>
      )}

      {/* ==========================================
      // 🚚 MODAL 3: CONFERÊNCIA FÍSICA NO RECEBIMENTO
      // ========================================== */}
      {receivingOrder && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col max-h-[85vh] animate-in fade-in zoom-in duration-200 overflow-hidden">
            
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex items-center gap-3 shrink-0 bg-slate-50">
              <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center border border-emerald-100">
                <Truck size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">Conferir Recebimento</h3>
                <p className="text-xs text-slate-400">Verifique os itens entregues e altere caso haja faltas</p>
              </div>
            </div>

            {/* Listagem de itens para conferência */}
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-left text-xs border-collapse">
                  <thead className="bg-slate-50 text-slate-500 font-bold border-b border-slate-200">
                    <tr>
                      <th className="p-3">Item</th>
                      <th className="p-3 text-right">Comprado</th>
                      <th className="p-3 text-center w-36">Quantidade Recebida</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {receivingOrder.items.map((item) => {
                      const recQty = receivedQtyMap[item.supply_id] !== undefined ? receivedQtyMap[item.supply_id] : 0;
                      const hasDiscrepancy = recQty !== item.ordered_quantity;
                      const upc = Number(item.unidades_por_compra) || 1;

                      return (
                        <tr key={item.supply_id} className={`hover:bg-slate-50/50 ${hasDiscrepancy ? "bg-amber-50/20" : ""}`}>
                          <td className="p-3">
                            <span className="font-bold text-slate-800 block">{item.name}</span>
                            {upc > 1 && (
                              <span className="text-[10px] text-slate-400 block mt-0.5">
                                Embalagem c/ {upc} {item.unit}
                              </span>
                            )}
                            {hasDiscrepancy && (
                              <span className="text-[8px] bg-amber-100 text-amber-800 px-1 py-0.5 rounded font-bold uppercase">Entrega Divergente</span>
                            )}
                          </td>
                          <td className="p-3 text-right font-mono text-slate-500 whitespace-nowrap">
                            {item.ordered_quantity} emb. <span className="text-[10px] text-slate-400">({item.ordered_quantity * upc} {item.unit})</span>
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex flex-col items-center">
                              <div className="flex items-center justify-between border border-slate-200 rounded-lg p-1 w-full bg-slate-50 max-w-[120px]">
                                <button
                                  type="button"
                                  onClick={() => setReceivedQtyMap((prev) => ({
                                    ...prev,
                                    [item.supply_id]: Math.max(0, (prev[item.supply_id] || 0) - 1)
                                  }))}
                                  className="w-6 h-6 rounded bg-white text-slate-600 border active:bg-slate-100 flex items-center justify-center font-bold text-xs"
                                >
                                  -
                                </button>
                                <input
                                  type="number"
                                  min="0"
                                  step="any"
                                  value={recQty}
                                  onChange={(e) => {
                                    const val = e.target.value === "" ? 0 : Number(e.target.value);
                                    setReceivedQtyMap((prev) => ({
                                      ...prev,
                                      [item.supply_id]: val
                                    }));
                                  }}
                                  className="w-10 bg-transparent text-center font-mono font-bold text-slate-800 text-sm outline-none"
                                />
                                <button
                                  type="button"
                                  onClick={() => setReceivedQtyMap((prev) => ({
                                    ...prev,
                                    [item.supply_id]: (prev[item.supply_id] || 0) + 1
                                  }))}
                                  className="w-6 h-6 rounded bg-white text-slate-600 border active:bg-slate-100 flex items-center justify-center font-bold text-xs"
                                >
                                  +
                                </button>
                              </div>
                              {upc > 1 && (
                                <span className="text-[9px] text-slate-400 mt-1 font-semibold">
                                  = {recQty * upc} {item.unit} no estoque
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Informação sobre atualização atômica */}
              <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-xl text-xs flex gap-3">
                <AlertCircle size={18} className="shrink-0 text-blue-600" />
                <p>
                  Confirmando o recebimento, o estoque atual destes itens sofrerá um incremento correspondente às quantidades recebidas indicadas na tabela acima.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-100 shrink-0 bg-slate-50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setReceivingOrder(null)}
                className="px-5 py-2.5 font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-xl transition-colors text-sm"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmReceipt}
                className="px-6 py-2.5 font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-xl transition-all shadow-md active:scale-[0.98] text-sm"
              >
                Confirmar e Somar ao Estoque
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
